<template>
  <v-container class="prompt-triage-page" fluid>
    <header class="page-header">
      <div>
        <h1 class="page-title">Feedback Triage</h1>
        <p class="page-subtitle">
          Curate which user feedback rows feed the prompt-autotune agent. Exclude fraudulent, duplicate, or out-of-scope
          rows before sending a batch to the agent.
        </p>
      </div>
      <v-btn :to="{ name: 'prompt-tuning-queue' }" prepend-icon="mdi-clipboard-list-outline" variant="outlined">
        Prompt proposals
      </v-btn>
    </header>

    <!-- Bucket cards: one per (client_company, config_key) with pending rows. -->
    <section v-if="buckets.length" class="bucket-grid">
      <article
        v-for="bucket in buckets"
        :key="`${bucket.clientCompanyId}:${bucket.configKey}`"
        class="bucket-card"
        :class="{ 'is-selected': isBucketSelected(bucket) }"
        @click="selectBucket(bucket)">
        <header class="bucket-card__header">
          <span class="bucket-card__client">{{ bucket.clientCompanyName || "Unknown client" }}</span>
          <code class="bucket-card__key">{{ configKeyLabel(bucket.configKey) }}</code>
        </header>
        <div class="bucket-card__counts">
          <span class="count count--pending">{{ bucket.counts.pending || 0 }} pending</span>
          <span class="count count--included">{{ bucket.counts.included || 0 }} included</span>
          <span class="count count--excluded">{{ bucket.counts.excluded || 0 }} excluded</span>
        </div>
        <v-btn
          color="primary"
          :disabled="!bucket.counts.included"
          :loading="sendingBucket === bucketKey(bucket)"
          variant="flat"
          density="comfortable"
          block
          @click.stop="confirmSend(bucket)">
          Send {{ bucket.counts.included || 0 }} included to autotune
        </v-btn>
      </article>
    </section>

    <!-- Filters + table -->
    <section class="filter-bar">
      <v-select
        v-model="filters.status"
        :items="statusOptions"
        label="Status"
        clearable
        density="compact"
        hide-details
        variant="outlined" />
      <v-select
        v-model="filters.clientCompanyId"
        :items="clientCompanyOptions"
        item-title="label"
        item-value="value"
        label="Client"
        clearable
        density="compact"
        hide-details
        variant="outlined" />
      <v-select
        v-model="filters.configKey"
        :items="configKeyOptions"
        item-title="label"
        item-value="value"
        label="Prompt"
        clearable
        density="compact"
        hide-details
        variant="outlined" />
      <v-select
        v-model="filters.exclusionReason"
        :items="exclusionReasonOptions"
        label="Excl. reason"
        clearable
        density="compact"
        hide-details
        variant="outlined" />
      <v-text-field
        v-model="filters.q"
        label="Search feedback / field / values"
        density="compact"
        hide-details
        variant="outlined"
        prepend-inner-icon="mdi-magnify"
        clearable />
    </section>

    <v-data-table-server
      v-model="selectedIds"
      :headers="tableHeaders"
      :items="rows"
      :items-length="totalRows"
      :loading="loading"
      item-value="id"
      show-select
      show-expand
      :items-per-page="pageSize"
      :page="page"
      class="triage-table"
      @update:page="onPageChange"
      @update:items-per-page="onPageSizeChange">
      <template #[`item.status`]="{ item }">
        <span class="status-chip" :class="`status-chip--${item.status.toLowerCase()}`">
          {{ item.status }}
        </span>
      </template>
      <template #[`item.field`]="{ item }">
        <code class="mono">{{ item.feedback?.fieldName || "—" }}</code>
      </template>
      <template #[`item.diff`]="{ item }">
        <div class="diff-cell">
          <div class="diff-cell__orig" :title="item.feedback?.originalValue">
            {{ truncate(item.feedback?.originalValue, 60) }}
          </div>
          <div class="diff-cell__arrow">→</div>
          <div class="diff-cell__new" :title="item.feedback?.newValue">
            {{ truncate(item.feedback?.newValue, 60) }}
          </div>
        </div>
      </template>
      <template #[`item.feedback_text`]="{ item }">
        <div class="feedback-text" :title="item.feedback?.feedback">
          {{ truncate(item.feedback?.feedback, 80) }}
        </div>
      </template>
      <template #[`item.exclusion_reason`]="{ item }">
        <span v-if="item.exclusionReason" class="exclusion-tag">
          {{ item.exclusionReason }}
        </span>
      </template>
      <template #[`item.order`]="{ item }">
        <a
          v-if="item.feedback?.orderKey"
          :href="`/check-my-coverage/${item.feedback.orderKey}`"
          target="_blank"
          class="order-link"
          @click.stop>
          <v-icon size="small">mdi-open-in-new</v-icon>
        </a>
      </template>
      <template #expanded-row="{ columns, item }">
        <tr class="expanded-row">
          <td :colspan="columns.length">
            <div class="expanded-row__content">
              <div class="expanded-row__block">
                <span class="expanded-row__label">Original value</span>
                <p class="expanded-row__value expanded-row__value--orig">{{ item.feedback?.originalValue || "—" }}</p>
              </div>
              <div class="expanded-row__block">
                <span class="expanded-row__label">New value</span>
                <p class="expanded-row__value expanded-row__value--new">{{ item.feedback?.newValue || "—" }}</p>
              </div>
              <div class="expanded-row__block expanded-row__block--wide">
                <span class="expanded-row__label">Reviewer feedback</span>
                <p class="expanded-row__value">{{ item.feedback?.feedback || "—" }}</p>
              </div>
            </div>
          </td>
        </tr>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-menu>
          <template #activator="{ props }">
            <v-btn
              icon="mdi-dots-vertical"
              size="small"
              variant="text"
              v-bind="props"
              :disabled="item.status === 'PROCESSED'" />
          </template>
          <v-list density="compact">
            <v-list-item
              :disabled="item.status === 'PROCESSED'"
              prepend-icon="mdi-check"
              title="Include"
              @click="markOne(item, 'INCLUDED')" />
            <v-list-item
              :disabled="item.status === 'PROCESSED'"
              prepend-icon="mdi-close"
              title="Exclude…"
              @click="openExcludeDialog([item.id])" />
          </v-list>
        </v-menu>
      </template>
    </v-data-table-server>

    <!-- Floating bulk actions bar -->
    <transition name="slide-up">
      <div v-if="selectedIds.length" class="bulk-bar">
        <span class="bulk-bar__counter"
          >{{ selectedIds.length }} row{{ selectedIds.length === 1 ? "" : "s" }} selected</span
        >
        <v-btn color="primary" variant="flat" prepend-icon="mdi-check" @click="bulkInclude"> Include selected </v-btn>
        <v-btn color="error" variant="flat" prepend-icon="mdi-close" @click="openExcludeDialog(selectedIds)">
          Exclude selected
        </v-btn>
        <v-btn variant="text" @click="selectedIds = []">Clear</v-btn>
      </div>
    </transition>

    <!-- Exclude reason dialog -->
    <v-dialog v-model="excludeDialog.open" max-width="540">
      <v-card>
        <v-card-title
          >Exclude {{ excludeDialog.ids.length }} feedback row{{
            excludeDialog.ids.length === 1 ? "" : "s"
          }}</v-card-title
        >
        <v-card-text>
          <v-select
            v-model="excludeDialog.reason"
            :items="exclusionReasonOptions"
            label="Reason"
            variant="outlined"
            density="comfortable" />
          <v-textarea v-model="excludeDialog.notes" label="Notes (optional)" variant="outlined" rows="3" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="excludeDialog.open = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="excludeDialog.saving"
            :disabled="!excludeDialog.reason"
            variant="flat"
            @click="applyExclude">
            Exclude
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Send to autotune confirm dialog -->
    <v-dialog v-model="sendDialog.open" max-width="640">
      <v-card v-if="sendDialog.bucket">
        <v-card-title>Send to autotune</v-card-title>
        <v-card-text>
          <div class="send-summary">
            <div><strong>Client:</strong> {{ sendDialog.bucket.clientCompanyName }}</div>
            <div><strong>Prompt:</strong> {{ configKeyLabel(sendDialog.bucket.configKey) }}</div>
            <div><strong>Included rows:</strong> {{ sendDialog.bucket.counts.included }}</div>
          </div>
          <p class="send-disclaimer">
            The agent will generate a proposal in the background and notify you when the FDE review page is ready.
            Excluded rows remain excluded; pending rows are not affected.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="sendDialog.open = false">Cancel</v-btn>
          <v-btn color="primary" :loading="sendDialog.sending" variant="flat" @click="sendToAutotune">
            Send to autotune
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
/* eslint-disable no-console -- error surfacing is via console for v1; UI snackbar TBD */
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { promptTuningTriage } from "@/services/promptTuningService";
import { CONFIG_KEYS, TRIAGE_STATUSES, EXCLUSION_REASONS, configKeyLabel } from "@/services/promptTuningConstants";

const router = useRouter();

const rows = ref([]);
const totalRows = ref(0);
const loading = ref(false);
const page = ref(1);
const pageSize = ref(50);
const selectedIds = ref([]);

const buckets = ref([]);
const sendingBucket = ref(null);

const filters = reactive({
  status: "PENDING",
  clientCompanyId: null,
  configKey: null,
  exclusionReason: null,
  q: "",
});

const excludeDialog = reactive({
  open: false,
  ids: [],
  reason: "",
  notes: "",
  saving: false,
});

const sendDialog = reactive({
  open: false,
  bucket: null,
  sending: false,
});

const statusOptions = TRIAGE_STATUSES;
const exclusionReasonOptions = EXCLUSION_REASONS;
const configKeyOptions = CONFIG_KEYS;

const clientCompanyOptions = computed(() => {
  const seen = new Map();
  for (const b of buckets.value) {
    if (!seen.has(b.clientCompanyId)) {
      seen.set(b.clientCompanyId, { value: b.clientCompanyId, label: b.clientCompanyName });
    }
  }
  return Array.from(seen.values());
});

const tableHeaders = [
  { title: "", key: "data-table-expand", width: 40 },
  { title: "Status", key: "status", width: 110 },
  { title: "Field", key: "field" },
  { title: "Correction", key: "diff" },
  { title: "Reviewer feedback", key: "feedback_text" },
  { title: "Excl.", key: "exclusion_reason", width: 110 },
  { title: "Order", key: "order", width: 60, sortable: false },
  { title: "", key: "actions", width: 50, sortable: false },
];

const bucketKey = (b) => `${b.clientCompanyId}:${b.configKey}`;
const isBucketSelected = (b) => filters.clientCompanyId === b.clientCompanyId && filters.configKey === b.configKey;

function truncate(value, n) {
  if (value == null || value === "") return "—";
  const s = String(value);
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

async function loadBuckets() {
  try {
    buckets.value = (await promptTuningTriage.summary()) || [];
  } catch (err) {
    console.error("Failed to load triage summary", err);
  }
}

async function loadRows() {
  loading.value = true;
  try {
    const params = {
      page: page.value,
      page_size: pageSize.value,
    };
    if (filters.status) params.status = filters.status;
    if (filters.clientCompanyId) params.client_company_id = filters.clientCompanyId;
    if (filters.configKey) params.config_key = filters.configKey;
    if (filters.exclusionReason) params.exclusion_reason = filters.exclusionReason;
    if (filters.q) params.q = filters.q;
    const response = await promptTuningTriage.list(params);
    rows.value = response.results || [];
    totalRows.value = response.count || 0;
  } catch (err) {
    console.error("Failed to load triage rows", err);
  } finally {
    loading.value = false;
  }
}

function selectBucket(b) {
  filters.clientCompanyId = b.clientCompanyId;
  filters.configKey = b.configKey;
  filters.status = "PENDING";
  page.value = 1;
}

function onPageChange(p) {
  page.value = p;
}

function onPageSizeChange(s) {
  pageSize.value = s;
  page.value = 1;
}

async function markOne(item, status, exclusionReason = "", notes = "") {
  try {
    await promptTuningTriage.mark(item.id, { status, exclusion_reason: exclusionReason, notes });
    await Promise.all([loadRows(), loadBuckets()]);
  } catch (err) {
    console.error("Failed to mark triage row", err);
  }
}

async function bulkInclude() {
  if (!selectedIds.value.length) return;
  try {
    await promptTuningTriage.bulkMark({ ids: selectedIds.value, status: "INCLUDED" });
    selectedIds.value = [];
    await Promise.all([loadRows(), loadBuckets()]);
  } catch (err) {
    console.error("Bulk include failed", err);
  }
}

function openExcludeDialog(ids) {
  excludeDialog.ids = [...ids];
  excludeDialog.reason = "";
  excludeDialog.notes = "";
  excludeDialog.open = true;
}

async function applyExclude() {
  if (!excludeDialog.reason || !excludeDialog.ids.length) return;
  excludeDialog.saving = true;
  try {
    await promptTuningTriage.bulkMark({
      ids: excludeDialog.ids,
      status: "EXCLUDED",
      exclusion_reason: excludeDialog.reason,
      notes: excludeDialog.notes,
    });
    selectedIds.value = [];
    excludeDialog.open = false;
    await Promise.all([loadRows(), loadBuckets()]);
  } catch (err) {
    console.error("Bulk exclude failed", err);
  } finally {
    excludeDialog.saving = false;
  }
}

function confirmSend(bucket) {
  sendDialog.bucket = bucket;
  sendDialog.open = true;
}

async function sendToAutotune() {
  if (!sendDialog.bucket) return;
  sendDialog.sending = true;
  sendingBucket.value = bucketKey(sendDialog.bucket);
  try {
    const proposal = await promptTuningTriage.sendToAutotune({
      client_company_id: sendDialog.bucket.clientCompanyId,
      config_key: sendDialog.bucket.configKey,
    });
    sendDialog.open = false;
    router.push({ name: "prompt-tuning-detail", params: { id: proposal.id } });
  } catch (err) {
    console.error("Send to autotune failed", err);
  } finally {
    sendDialog.sending = false;
    sendingBucket.value = null;
  }
}

// Debounce the search input (`filters.q`) so typing doesn't hammer the API per
// keystroke. Dropdowns reload immediately — those are infrequent and the user
// expects instant feedback.
let _searchTimer = null;
watch(
  () => filters.q,
  () => {
    clearTimeout(_searchTimer);
    _searchTimer = setTimeout(() => {
      page.value = 1;
      loadRows();
    }, 250);
  }
);
watch(
  () => [filters.status, filters.clientCompanyId, filters.configKey, filters.exclusionReason],
  () => {
    page.value = 1;
    loadRows();
  }
);

onMounted(() => {
  loadBuckets();
  loadRows();
});
</script>

<style lang="scss" scoped>
.prompt-triage-page {
  padding: 24px 32px 96px;
  max-width: 1600px;

  @media (max-width: 600px) {
    padding: 16px 16px 96px;
  }
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
}

.page-title {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.page-subtitle {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin: 6px 0 0;
  max-width: 720px;
}

.bucket-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.bucket-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    border-color: rgba(0, 0, 0, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }

  &.is-selected {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
  }
}

.bucket-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.bucket-card__client {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.bucket-card__key {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  background: rgba(0, 0, 0, 0.04);
  padding: 2px 6px;
  border-radius: 4px;
}

.bucket-card__counts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
}

.count {
  padding: 2px 8px;
  border-radius: 4px;

  &--pending {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.7);
  }

  &--included {
    background: rgba(34, 139, 230, 0.1);
    color: rgb(20, 110, 200);
  }

  &--excluded {
    background: rgba(212, 70, 70, 0.1);
    color: rgb(180, 50, 50);
  }
}

.filter-bar {
  display: grid;
  grid-template-columns: 180px 220px 220px 180px 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.triage-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: #ffffff;
}

.status-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;

  &--pending {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.65);
  }

  &--included {
    background: rgba(34, 139, 230, 0.12);
    color: rgb(20, 110, 200);
  }

  &--excluded {
    background: rgba(212, 70, 70, 0.12);
    color: rgb(180, 50, 50);
  }

  &--processed {
    background: rgba(0, 0, 0, 0.85);
    color: rgba(255, 255, 255, 0.92);
  }
}

.exclusion-tag {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  background: rgba(212, 70, 70, 0.08);
  color: rgb(160, 40, 40);
  padding: 2px 6px;
  border-radius: 3px;
}

.mono {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
}

.diff-cell {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;

  &__orig {
    color: rgba(180, 50, 50, 0.85);
    text-decoration: line-through;
    text-decoration-color: rgba(180, 50, 50, 0.4);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    color: rgba(0, 0, 0, 0.4);
  }

  &__new {
    color: rgb(20, 110, 200);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.feedback-text {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}

.expanded-row {
  td {
    padding: 14px 16px 16px;
    background: rgba(0, 0, 0, 0.02);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
}

.expanded-row__content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    position: sticky;
    left: 16px;
    max-width: calc(100vw - 64px);
  }
}

.expanded-row__block--wide {
  grid-column: 1 / -1;
}

.expanded-row__label {
  display: block;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
}

.expanded-row__value {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.8);
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;

  &--orig {
    color: rgba(180, 50, 50, 0.85);
  }

  &--new {
    color: rgb(20, 110, 200);
  }
}

.order-link {
  display: inline-flex;
  color: rgba(0, 0, 0, 0.5);
  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}

.bulk-bar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  background: rgba(20, 20, 20, 0.94);
  color: #ffffff;
  border-radius: 8px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  z-index: 10;
  font-family: "DM Sans", system-ui, sans-serif;
  max-width: calc(100vw - 24px);
  flex-wrap: wrap;
  justify-content: center;

  &__counter {
    font-size: 13px;
    font-weight: 500;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 180ms ease, opacity 180ms ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translate(-50%, 20px);
  opacity: 0;
}

.send-summary {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 14px;
  display: grid;
  gap: 6px;
  margin-bottom: 12px;
}

.send-disclaimer {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}
</style>
